import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./LaunchpadManagementPage.css";

const STATUS_OPTIONS_EDIT = ["LIVE", "UPCOMING", "ENDED", "CANCELLED"];
const DESCRIPTION_MAX = 5000;
const TOTAL_SUPPLY_MAX = 50;

const urlValid = (v) => {
  if (!v || v.trim() === "") return true;
  try {
    new URL(v.trim());
    return true;
  } catch {
    return false;
  }
};

/** Returns min value for datetime-local (no past dates) */
const getMinStartTime = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Returns min for end time (1 minute after start, format for datetime-local) */
const getMinEndTime = (startTime) => {
  if (!startTime) return undefined;
  const d = new Date(startTime);
  d.setMinutes(d.getMinutes() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultForm = {
  base_currency_id: "",
  quote_currency_id: "",
  tokensForSale: "",
  minPurchase: "",
  maxPurchase: "",
  tokenPrice: "",
  startTime: "",
  endTime: "",
  totalSupply: "",
  website: "",
  whitepaper: "",
  description: "",
};

const LaunchpadManagementPage = () => {
  const [activeTab, setActiveTab] = useState("create");
  const [form, setForm] = useState(defaultForm);
  const [formErrors, setFormErrors] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [list, setList] = useState([]);
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({ total: 0, upcoming: 0, live: 0, ended: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [listFilter, setListFilter] = useState("All");
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [confirmModal, setConfirmModal] = useState(null);

  const updateForm = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFormErrors((e) => ({ ...e, [key]: undefined }));
  };

  const clearMessage = () => setMessage({ type: "", text: "" });

  const fetchCurrencies = useCallback(async () => {
    const res = await AdminService.getLaunchpadCurrencies();
    if (res?.success && Array.isArray(res.data)) {
      setCurrencies(res.data);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    const res = await AdminService.getLaunchpadSummary();
    if (res?.success && res.data) {
      const d = res.data;
      setSummary({
        total: Number(d.total ?? d.totalLaunchpads ?? d.totalCount) || 0,
        upcoming: Number(d.upcoming ?? d.upcomingCount) || 0,
        live: Number(d.live ?? d.liveCount) || 0,
        ended: Number(d.ended ?? d.endedCount) || 0,
      });
    } else {
      setSummary({ total: 0, upcoming: 0, live: 0, ended: 0 });
    }
  }, []);

  const fetchListing = useCallback(async () => {
    setLoading(true);
    const params = { page: pagination.page, limit: pagination.limit };
    if (listFilter !== "All") params.status = listFilter;
    const res = await AdminService.getLaunchpadListing(params);
    setLoading(false);
    if (res?.success && Array.isArray(res.data)) {
      setList(res.data);
      if (res.pagination) {
        setPagination((p) => ({
          ...p,
          total: res.pagination.total ?? 0,
          totalPages: res.pagination.totalPages ?? 0,
        }));
      }
    } else {
      setList([]);
    }
  }, [listFilter, pagination.page, pagination.limit]);

  const fetchSales = useCallback(async () => {
    const res = await AdminService.getLaunchpadSalesTracking({ page: 1, limit: 50 });
    if (res?.success && Array.isArray(res.data)) {
      setSales(res.data);
    } else {
      setSales([]);
    }
  }, []);

  useEffect(() => { fetchCurrencies(); }, [fetchCurrencies]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  useEffect(() => {
    if (activeTab === "list") fetchListing();
    else if (activeTab === "sales") fetchSales();
  }, [activeTab, fetchListing, fetchSales]);


  const counts = {
    total: summary.total,
    upcoming: summary.upcoming,
    live: summary.live,
    ended: summary.ended,
  };

  const validateForm = (isEdit = false) => {
    const err = {};
    if (!form.base_currency_id) err.base_currency_id = "Select token (base currency)";
    if (!form.quote_currency_id) err.quote_currency_id = "Select payment currency";
    if (form.base_currency_id && form.quote_currency_id && form.base_currency_id === form.quote_currency_id) {
      err.quote_currency_id = "Base and quote currency must be different";
    }

    const tokensForSale = parseFloat(form.tokensForSale);
    if (isNaN(tokensForSale) || tokensForSale <= 0) err.tokensForSale = "Must be greater than 0";
    const minPurchase = parseFloat(form.minPurchase);
    if (isNaN(minPurchase) || minPurchase <= 0) err.minPurchase = "Must be greater than 0";
    const maxPurchase = parseFloat(form.maxPurchase);
    if (isNaN(maxPurchase) || maxPurchase <= 0) err.maxPurchase = "Must be greater than 0";
    if (minPurchase > 0 && maxPurchase < minPurchase) err.maxPurchase = "Must be ≥ Min Purchase";
    const tokenPrice = parseFloat(form.tokenPrice);
    if (isNaN(tokenPrice) || tokenPrice <= 0) err.tokenPrice = "Must be greater than 0";

    if (!form.startTime) err.startTime = "Start time is required";
    if (!form.endTime) err.endTime = "End time is required";
    if (form.startTime) {
      const startMs = new Date(form.startTime).getTime();
      if (startMs < Date.now()) err.startTime = "Start time must be in the future";
    }
    if (form.startTime && form.endTime) {
      const s = new Date(form.startTime).getTime();
      const e = new Date(form.endTime).getTime();
      if (e <= s) err.endTime = "End time must be after start time";
    }

    if (form.totalSupply && String(form.totalSupply).length > TOTAL_SUPPLY_MAX) {
      err.totalSupply = `Max ${TOTAL_SUPPLY_MAX} characters`;
    }
    if (form.website && !urlValid(form.website)) err.website = "Invalid URL";
    if (form.whitepaper && !urlValid(form.whitepaper)) err.whitepaper = "Invalid URL";
    if (form.description && form.description.length > DESCRIPTION_MAX) {
      err.description = `Max ${DESCRIPTION_MAX} characters`;
    }

    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const toCreatePayload = () => {
    const payload = {
      base_currency_id: form.base_currency_id,
      quote_currency_id: form.quote_currency_id,
      tokensForSale: parseFloat(form.tokensForSale),
      minPurchase: parseFloat(form.minPurchase),
      maxPurchase: parseFloat(form.maxPurchase),
      tokenPrice: parseFloat(form.tokenPrice),
      startTime: new Date(form.startTime).toISOString(),
      endTime: new Date(form.endTime).toISOString(),
    };
    if (form.totalSupply) payload.totalSupply = form.totalSupply;
    if (form.website?.trim()) payload.website = form.website.trim();
    if (form.whitepaper?.trim()) payload.whitepaper = form.whitepaper.trim();
    if (form.description) payload.description = form.description;
    return payload;
  };

  const handleReset = (e) => {
    e.preventDefault();
    setForm(defaultForm);
    setFormErrors({});
    clearMessage();
  };

  const handlePublish = (e) => {
    e.preventDefault();
    if (!validateForm(false)) return;
    setConfirmModal({ type: "create" });
  };

  const handleConfirmCreate = async () => {
    setSubmitLoading(true);
    clearMessage();
    setConfirmModal(null);
    const payload = toCreatePayload();
    const res = await AdminService.createLaunchpad(payload);
    setSubmitLoading(false);
    if (res?.success) {
      setMessage({ type: "success", text: res.message || "Launchpad created successfully" });
      setForm(defaultForm);
      fetchSummary();
      setActiveTab("list");
      fetchListing();
    } else {
      setMessage({ type: "error", text: res?.message || "Failed to create launchpad" });
    }
  };

  const handleStatusClick = (id, newStatus) => {
    const label = { LIVE: "Go Live", ENDED: "End", CANCELLED: "Cancel" }[newStatus] || newStatus;
    setConfirmModal({ type: "status", id, status: newStatus, label });
  };

  const handleConfirmStatus = async () => {
    const { id, status } = confirmModal;
    setConfirmModal(null);
    clearMessage();
    const res = await AdminService.updateLaunchpadStatus(id, status);
    if (res?.success) {
      setMessage({ type: "success", text: res.message || "Status updated" });
      fetchSummary();
      fetchListing();
    } else {
      setMessage({ type: "error", text: res?.message || "Failed to update status" });
    }
  };


  const openEdit = async (row) => {
    setEditId(row._id);
    setEditData(null);
    const res = await AdminService.getLaunchpadDetail(row._id);
    if (res?.success && res.data) {
      const d = res.data;
      const baseObj = typeof d.base_currency_id === "object" ? d.base_currency_id : null;
      const quoteObj = typeof d.quote_currency_id === "object" ? d.quote_currency_id : null;
      const baseId = baseObj?._id ?? d.base_currency_id ?? "";
      const quoteId = quoteObj?._id ?? d.quote_currency_id ?? "";
      const start = d.startTime ? new Date(d.startTime).toISOString().slice(0, 16) : "";
      const end = d.endTime ? new Date(d.endTime).toISOString().slice(0, 16) : "";
      setEditData({
        base_currency_id: baseId,
        quote_currency_id: quoteId,
        baseCurrency: baseObj,
        quoteCurrency: quoteObj,
        tokensForSale: d.tokensForSale ?? "",
        minPurchase: d.minPurchase ?? "",
        maxPurchase: d.maxPurchase ?? "",
        tokenPrice: d.tokenPrice ?? "",
        startTime: start,
        endTime: end,
        totalSupply: d.totalSupply ?? "",
        website: d.website ?? "",
        whitepaper: d.whitepaper ?? "",
        description: d.description ?? "",
        status: d.status ?? "UPCOMING",
      });
    } else {
      setEditData(null);
      setEditId(null);
    }
  };

  const closeEdit = () => {
    setEditId(null);
    setEditData(null);
    setFormErrors({});
    clearMessage();
  };

  const updateEditForm = (key, value) => {
    setEditData((d) => (d ? { ...d, [key]: value } : null));
    setFormErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validateEditForm = () => {
    if (!editData) return false;
    const err = {};
    const tokensForSale = parseFloat(editData.tokensForSale);
    if (isNaN(tokensForSale) || tokensForSale <= 0) err.tokensForSale = "Must be > 0";
    const minPurchase = parseFloat(editData.minPurchase);
    if (isNaN(minPurchase) || minPurchase <= 0) err.minPurchase = "Must be > 0";
    const maxPurchase = parseFloat(editData.maxPurchase);
    if (isNaN(maxPurchase) || maxPurchase <= 0) err.maxPurchase = "Must be > 0";
    if (minPurchase > 0 && maxPurchase < minPurchase) err.maxPurchase = "Must be ≥ Min Purchase";
    const tokenPrice = parseFloat(editData.tokenPrice);
    if (isNaN(tokenPrice) || tokenPrice <= 0) err.tokenPrice = "Must be > 0";
    if (!editData.startTime) err.startTime = "Required";
    if (!editData.endTime) err.endTime = "Required";
    if (editData.startTime && editData.endTime) {
      const s = new Date(editData.startTime).getTime();
      const e = new Date(editData.endTime).getTime();
      if (e <= s) err.endTime = "Must be after start time";
    }
    if (editData.website && !urlValid(editData.website)) err.website = "Invalid URL";
    if (editData.whitepaper && !urlValid(editData.whitepaper)) err.whitepaper = "Invalid URL";
    if (editData.description && editData.description.length > DESCRIPTION_MAX) err.description = `Max ${DESCRIPTION_MAX} chars`;
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editData || !editId || !validateEditForm()) return;
    setSubmitLoading(true);
    clearMessage();
    const payload = {
      tokensForSale: parseFloat(editData.tokensForSale),
      minPurchase: parseFloat(editData.minPurchase),
      maxPurchase: parseFloat(editData.maxPurchase),
      tokenPrice: parseFloat(editData.tokenPrice),
      startTime: new Date(editData.startTime).toISOString(),
      endTime: new Date(editData.endTime).toISOString(),
      status: editData.status,
    };
    if (editData.totalSupply) payload.totalSupply = editData.totalSupply;
    if (editData.website?.trim()) payload.website = editData.website.trim();
    if (editData.whitepaper?.trim()) payload.whitepaper = editData.whitepaper.trim();
    if (editData.description) payload.description = editData.description;

    const res = await AdminService.updateLaunchpad(editId, payload);
    setSubmitLoading(false);
    if (res?.success) {
      setMessage({ type: "success", text: res.message || "Launchpad updated" });
      closeEdit();
      fetchSummary();
      fetchListing();
    } else {
      setMessage({ type: "error", text: res?.message || "Failed to update" });
    }
  };

  const getTokenDisplay = (row) => {
    const base = row.base_currency_id;
    const name = typeof base === "object" ? base?.name : "";
    const symbol = typeof base === "object" ? base?.short_name : "";
    return { name: name || "—", symbol: symbol || "—" };
  };

  const getQuoteSymbol = (row) => {
    const q = row.quote_currency_id;
    return typeof q === "object" ? q?.short_name || "—" : "—";
  };

  const goToPage = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setPagination((p) => ({ ...p, page }));
  };

  return (
    <main className="dashboard-main dashboard-subpage launchpad-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Launchpad Management</span>
          </p>
          <h1 className="dashboard-hero__title">Launchpad Management</h1>
          <p className="dashboard-hero__subtitle">Create and manage token launchpads.</p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-rocket" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="launchpad-cards">
          <div className="launchpad-card">
            <i className="fas fa-list" />
            <span className="launchpad-card__value">{counts.total}</span>
            <span className="launchpad-card__label">Total</span>
          </div>
          <div className="launchpad-card">
            <i className="fas fa-clock" />
            <span className="launchpad-card__value">{counts.upcoming}</span>
            <span className="launchpad-card__label">Upcoming</span>
          </div>
          <div className="launchpad-card">
            <i className="fas fa-play" />
            <span className="launchpad-card__value">{counts.live}</span>
            <span className="launchpad-card__label">Live</span>
          </div>
          <div className="launchpad-card">
            <i className="fas fa-check" />
            <span className="launchpad-card__value">{counts.ended}</span>
            <span className="launchpad-card__label">Ended</span>
          </div>
        </div>

        <div className="launchpad-tabs">
          <button
            type="button"
            className={`launchpad-tab ${activeTab === "create" ? "launchpad-tab--active" : ""}`}
            onClick={() => { setActiveTab("create"); clearMessage(); }}
          >
            <i className="fas fa-plus" /> Create Launchpad
          </button>
          <button
            type="button"
            className={`launchpad-tab ${activeTab === "list" ? "launchpad-tab--active" : ""}`}
            onClick={() => { setActiveTab("list"); clearMessage(); }}
          >
            <i className="fas fa-list" /> Launchpad List
          </button>
          <button
            type="button"
            className={`launchpad-tab ${activeTab === "sales" ? "launchpad-tab--active" : ""}`}
            onClick={() => { setActiveTab("sales"); clearMessage(); }}
          >
            <i className="fas fa-chart-line" /> Sales Tracking List
          </button>
        </div>

        {message.text && (
          <div className={`launchpad-message launchpad-message--${message.type}`}>
            {message.text}
            <button type="button" className="launchpad-message__close" onClick={clearMessage} aria-label="Close">×</button>
          </div>
        )}

        <div className="launchpad-content">
          {activeTab === "create" && (
            <section className="launchpad-section">
              <h2 className="launchpad-section__title"><i className="fas fa-plus" /> Create New Launchpad</h2>
              <p className="launchpad-section__hint">Add coins via Currency Management first. Token name and symbol come from the selected base currency. Status is auto-computed from start/end time.</p>
              <form className="launchpad-form" onSubmit={handlePublish}>
                <div className="launchpad-form__block">
                  <h3>Currency Selection</h3>
                  <div className="launchpad-form__row">
                    <div className="launchpad-form__group">
                      <label>Token to Sell (Base) <span className="required">*</span></label>
                      <select
                        value={form.base_currency_id}
                        onChange={(e) => updateForm("base_currency_id", e.target.value)}
                        required
                        className={formErrors.base_currency_id ? "launchpad-form__error" : ""}
                      >
                        <option value="">Select base currency</option>
                        {currencies.map((c) => (
                          <option key={c._id} value={c._id}>{c.name} ({c.short_name})</option>
                        ))}
                      </select>
                      {formErrors.base_currency_id && <span className="launchpad-form__err-text">{formErrors.base_currency_id}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Payment Currency (Quote) <span className="required">*</span></label>
                      <select
                        value={form.quote_currency_id}
                        onChange={(e) => updateForm("quote_currency_id", e.target.value)}
                        required
                        className={formErrors.quote_currency_id ? "launchpad-form__error" : ""}
                      >
                        <option value="">Select quote currency</option>
                        {currencies.map((c) => (
                          <option key={c._id} value={c._id}>{c.name} ({c.short_name})</option>
                        ))}
                      </select>
                      {formErrors.quote_currency_id && <span className="launchpad-form__err-text">{formErrors.quote_currency_id}</span>}
                    </div>
                  </div>
                </div>

                <div className="launchpad-form__block">
                  <h3>Sale Configuration</h3>
                  <div className="launchpad-form__grid">
                    <div className="launchpad-form__group">
                      <label>Tokens For Sale <span className="required">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0.000001"
                        placeholder="e.g. 1000000"
                        value={form.tokensForSale}
                        onChange={(e) => updateForm("tokensForSale", e.target.value)}
                        className={formErrors.tokensForSale ? "launchpad-form__error" : ""}
                      />
                      {formErrors.tokensForSale && <span className="launchpad-form__err-text">{formErrors.tokensForSale}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Min Purchase <span className="required">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0.000001"
                        placeholder="e.g. 10"
                        value={form.minPurchase}
                        onChange={(e) => updateForm("minPurchase", e.target.value)}
                        className={formErrors.minPurchase ? "launchpad-form__error" : ""}
                      />
                      {formErrors.minPurchase && <span className="launchpad-form__err-text">{formErrors.minPurchase}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Max Purchase <span className="required">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0.000001"
                        placeholder="e.g. 10000"
                        value={form.maxPurchase}
                        onChange={(e) => updateForm("maxPurchase", e.target.value)}
                        className={formErrors.maxPurchase ? "launchpad-form__error" : ""}
                      />
                      {formErrors.maxPurchase && <span className="launchpad-form__err-text">{formErrors.maxPurchase}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Token Price <span className="required">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0.000001"
                        placeholder="e.g. 0.001"
                        value={form.tokenPrice}
                        onChange={(e) => updateForm("tokenPrice", e.target.value)}
                        className={formErrors.tokenPrice ? "launchpad-form__error" : ""}
                      />
                      {formErrors.tokenPrice && <span className="launchpad-form__err-text">{formErrors.tokenPrice}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>Total Supply (optional)</label>
                      <input
                        type="text"
                        placeholder="Max 50 chars"
                        maxLength={TOTAL_SUPPLY_MAX}
                        value={form.totalSupply}
                        onChange={(e) => updateForm("totalSupply", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="launchpad-form__block">
                  <h3>Time Configuration</h3>
                  <div className="launchpad-form__row">
                    <div className="launchpad-form__group">
                      <label>Start Time <span className="required">*</span></label>
                      <input
                        type="datetime-local"
                        value={form.startTime}
                        onChange={(e) => updateForm("startTime", e.target.value)}
                        min={getMinStartTime()}
                        className={formErrors.startTime ? "launchpad-form__error" : ""}
                      />
                      <span className="launchpad-form__hint">Status is auto-computed: past start → LIVE/ENDED, future → UPCOMING</span>
                      {formErrors.startTime && <span className="launchpad-form__err-text">{formErrors.startTime}</span>}
                    </div>
                    <div className="launchpad-form__group">
                      <label>End Time <span className="required">*</span></label>
                      <input
                        type="datetime-local"
                        value={form.endTime}
                        onChange={(e) => updateForm("endTime", e.target.value)}
                        min={getMinEndTime(form.startTime)}
                        className={formErrors.endTime ? "launchpad-form__error" : ""}
                      />
                      <span className="launchpad-form__hint">Must be after start time</span>
                      {formErrors.endTime && <span className="launchpad-form__err-text">{formErrors.endTime}</span>}
                    </div>
                  </div>
                </div>

                <div className="launchpad-form__block">
                  <h3>Additional Links & Media</h3>
                  <div className="launchpad-form__group">
                    <label>Website (optional)</label>
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={form.website}
                      onChange={(e) => updateForm("website", e.target.value)}
                      className={formErrors.website ? "launchpad-form__error" : ""}
                    />
                    {formErrors.website && <span className="launchpad-form__err-text">{formErrors.website}</span>}
                  </div>
                  <div className="launchpad-form__group">
                    <label>Whitepaper (optional)</label>
                    <input
                      type="url"
                      placeholder="https://example.com/whitepaper.pdf"
                      value={form.whitepaper}
                      onChange={(e) => updateForm("whitepaper", e.target.value)}
                      className={formErrors.whitepaper ? "launchpad-form__error" : ""}
                    />
                    {formErrors.whitepaper && <span className="launchpad-form__err-text">{formErrors.whitepaper}</span>}
                  </div>
                </div>

                <div className="launchpad-form__group">
                  <label>Description (optional, max {DESCRIPTION_MAX} chars)</label>
                  <textarea
                    placeholder="Project description..."
                    value={form.description}
                    onChange={(e) => updateForm("description", e.target.value)}
                    rows={5}
                    maxLength={DESCRIPTION_MAX}
                    className={formErrors.description ? "launchpad-form__error" : ""}
                  />
                  <span className="launchpad-form__char-count">{form.description.length}/{DESCRIPTION_MAX}</span>
                  {formErrors.description && <span className="launchpad-form__err-text">{formErrors.description}</span>}
                </div>

                <div className="launchpad-form__actions">
                  <button type="button" className="launchpad-btn launchpad-btn--reset" onClick={handleReset}>Reset</button>
                  <button type="submit" className="launchpad-btn launchpad-btn--publish" disabled={submitLoading}>
                    {submitLoading ? <><i className="fas fa-spinner fa-spin" /> Creating...</> : <><i className="fas fa-rocket" /> Publish Launchpad</>}
                  </button>
                </div>
              </form>
            </section>
          )}

          {activeTab === "list" && (
            <section className="launchpad-section">
              <h2 className="launchpad-section__title"><i className="fas fa-list" /> Launchpad List</h2>
              <div className="launchpad-list-tabs">
                <button type="button" className={`launchpad-list-tab ${listFilter === "All" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("All"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  All
                </button>
                <button type="button" className={`launchpad-list-tab ${listFilter === "UPCOMING" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("UPCOMING"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  Upcoming
                </button>
                <button type="button" className={`launchpad-list-tab ${listFilter === "LIVE" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("LIVE"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  Live
                </button>
                <button type="button" className={`launchpad-list-tab ${listFilter === "ENDED" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("ENDED"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  Ended
                </button>
                <button type="button" className={`launchpad-list-tab ${listFilter === "CANCELLED" ? "launchpad-list-tab--active" : ""}`} onClick={() => { setListFilter("CANCELLED"); setPagination((p) => ({ ...p, page: 1 })); }}>
                  Cancelled
                </button>
              </div>

              {loading ? (
                <div className="launchpad-loading"><i className="fas fa-spinner fa-spin" /> Loading...</div>
              ) : (
                <>
                  <div className="launchpad-table-wrap">
                    <table className="launchpad-table">
                      <thead>
                        <tr>
                          <th>Token</th>
                          <th>Quote</th>
                          <th>Start Time</th>
                          <th>End Time</th>
                          <th>Total Supply</th>
                          <th>Tokens For Sale</th>
                          <th>Price</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.length === 0 ? (
                          <tr><td colSpan="9" className="launchpad-table__empty">No launchpads found</td></tr>
                        ) : (
                          list.map((row) => {
                            const { name, symbol } = getTokenDisplay(row);
                            const quoteSymbol = getQuoteSymbol(row);
                            const status = (row?.status || "").toUpperCase();
                            const startStr = row.startTime ? new Date(row.startTime).toLocaleString() : "—";
                            const endStr = row.endTime ? new Date(row.endTime).toLocaleString() : "—";
                            return (
                              <tr key={row._id}>
                                <td><strong>{name}</strong> ({symbol})</td>
                                <td>{quoteSymbol}</td>
                                <td>{startStr}</td>
                                <td>{endStr}</td>
                                <td>{row.totalSupply || "—"}</td>
                                <td>{row.tokensForSale ?? "—"}</td>
                                <td>{row.tokenPrice != null ? `${row.tokenPrice} ${quoteSymbol}` : "—"}</td>
                                <td><span className={`launchpad-status launchpad-status--${status.toLowerCase()}`}>{row.status || status}</span></td>
                                <td>
                                  <div className="launchpad-action-btns">
                                    {status !== "ENDED" && status !== "CANCELLED" && (
                                      <button type="button" className="btn btn-sm btn-info" onClick={() => openEdit(row)}>Edit</button>
                                    )}
                                    {status === "UPCOMING" && (
                                      <>
                                        <button type="button" className="btn btn-sm btn-success" onClick={() => handleStatusClick(row._id, "LIVE")}>Go Live</button>
                                        <button type="button" className="btn btn-sm btn-danger" onClick={() => handleStatusClick(row._id, "CANCELLED")}>Cancel</button>
                                      </>
                                    )}
                                    {(status === "LIVE") && (
                                      <>
                                        <button type="button" className="btn btn-sm btn-warning" onClick={() => handleStatusClick(row._id, "ENDED")}>End</button>
                                        <button type="button" className="btn btn-sm btn-danger" onClick={() => handleStatusClick(row._id, "CANCELLED")}>Cancel</button>
                                      </>
                                    )}
                                    {(status === "ENDED" || status === "CANCELLED") && (
                                      <span className={`launchpad-action-status launchpad-action-status--${status === "ENDED" ? "ended" : "cancelled"}`}>{row.status}</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {pagination.totalPages > 1 && (
                    <div className="launchpad-pagination">
                      <button type="button" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1}>Prev</button>
                      <span>Page {pagination.page} of {pagination.totalPages}</span>
                      <button type="button" onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}>Next</button>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {activeTab === "sales" && (
            <section className="launchpad-section">
              <h2 className="launchpad-section__title"><i className="fas fa-chart-line" /> Sales Tracking List</h2>
              <div className="launchpad-table-wrap">
                <table className="launchpad-table">
                  <thead>
                    <tr>
                      <th>Launchpad / Token</th>
                      <th>Sold</th>
                      <th>Total For Sale</th>
                      <th>Raised</th>
                      <th>Participants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.length === 0 ? (
                      <tr><td colSpan="5" className="launchpad-table__empty">No sales data</td></tr>
                    ) : (
                      sales.map((row) => {
                        const { name, symbol } = getTokenDisplay(row);
                        const quoteSymbol = getQuoteSymbol(row);
                        return (
                          <tr key={row._id}>
                            <td><strong>{name}</strong> ({symbol})</td>
                            <td>{row.sold ?? row.availableTokens != null ? (row.tokensForSale - (row.availableTokens ?? 0)) : "—"}</td>
                            <td>{row.tokensForSale ?? "—"}</td>
                            <td>{row.totalRaised ?? row.raised ?? "—"} {quoteSymbol}</td>
                            <td>{row.participantsCount ?? row.participants ?? "—"}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="launchpad-confirm-overlay" onClick={() => setConfirmModal(null)}>
          <div className="launchpad-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="launchpad-confirm-title">
              <i className="fas fa-exclamation-triangle" />
              {confirmModal.type === "create" ? "Create Launchpad" : "Update Status"}
            </h3>
            <p className="launchpad-confirm-text">
              {confirmModal.type === "create"
                ? "Are you sure you want to create this launchpad? This action cannot be undone."
                : `Are you sure you want to ${confirmModal.label || confirmModal.status} this launchpad?`}
            </p>
            <div className="launchpad-confirm-actions">
              <button type="button" className="launchpad-btn launchpad-btn--reset" onClick={() => setConfirmModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="launchpad-btn launchpad-btn--publish"
                onClick={confirmModal.type === "create" ? handleConfirmCreate : handleConfirmStatus}
                disabled={submitLoading && confirmModal.type === "create"}
              >
                {submitLoading && confirmModal.type === "create" ? (
                  <><i className="fas fa-spinner fa-spin" /> Creating...</>
                ) : (
                  <><i className="fas fa-check" /> Confirm</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editId && editData && (
        <div className="launchpad-modal-overlay" onClick={closeEdit}>
          <div className="launchpad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="launchpad-modal__header">
              <h3>Edit Launchpad</h3>
              <button type="button" className="launchpad-modal__close" onClick={closeEdit} aria-label="Close">×</button>
            </div>
            <form className="launchpad-modal__body" onSubmit={handleSaveEdit}>
              <div className="launchpad-form__row">
                <div className="launchpad-form__group">
                  <label>Base Currency</label>
                  <input
                    type="text"
                    value={editData.baseCurrency ? `${editData.baseCurrency.name} (${editData.baseCurrency.short_name})` : editData.base_currency_id || "—"}
                    readOnly
                    className="launchpad-form__readonly"
                  />
                  <span className="launchpad-form__hint">Currency cannot be changed after creation</span>
                </div>
                <div className="launchpad-form__group">
                  <label>Quote Currency</label>
                  <input
                    type="text"
                    value={editData.quoteCurrency ? `${editData.quoteCurrency.name} (${editData.quoteCurrency.short_name})` : editData.quote_currency_id || "—"}
                    readOnly
                    className="launchpad-form__readonly"
                  />
                  <span className="launchpad-form__hint">Currency cannot be changed after creation</span>
                </div>
              </div>
              <div className="launchpad-form__grid">
                <div className="launchpad-form__group">
                  <label>Tokens For Sale</label>
                  <input type="number" step="any" min="0.000001" value={editData.tokensForSale} onChange={(e) => updateEditForm("tokensForSale", e.target.value)} />
                </div>
                <div className="launchpad-form__group">
                  <label>Min Purchase</label>
                  <input type="number" step="any" min="0.000001" value={editData.minPurchase} onChange={(e) => updateEditForm("minPurchase", e.target.value)} />
                </div>
                <div className="launchpad-form__group">
                  <label>Max Purchase</label>
                  <input type="number" step="any" min="0.000001" value={editData.maxPurchase} onChange={(e) => updateEditForm("maxPurchase", e.target.value)} />
                </div>
                <div className="launchpad-form__group">
                  <label>Token Price</label>
                  <input type="number" step="any" min="0.000001" value={editData.tokenPrice} onChange={(e) => updateEditForm("tokenPrice", e.target.value)} />
                </div>
                <div className="launchpad-form__group">
                  <label>Status</label>
                  <select value={editData.status} onChange={(e) => updateEditForm("status", e.target.value)}>
                    {STATUS_OPTIONS_EDIT.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="launchpad-form__row">
                <div className="launchpad-form__group">
                  <label>Start Time</label>
                  <input type="datetime-local" value={editData.startTime} onChange={(e) => updateEditForm("startTime", e.target.value)} />
                </div>
                <div className="launchpad-form__group">
                  <label>End Time</label>
                  <input type="datetime-local" value={editData.endTime} onChange={(e) => updateEditForm("endTime", e.target.value)} min={getMinEndTime(editData.startTime)} />
                </div>
              </div>
              <div className="launchpad-form__group">
                <label>Total Supply</label>
                <input type="text" maxLength={TOTAL_SUPPLY_MAX} value={editData.totalSupply} onChange={(e) => updateEditForm("totalSupply", e.target.value)} />
              </div>
              <div className="launchpad-form__group">
                <label>Website</label>
                <input type="url" placeholder="https://" value={editData.website} onChange={(e) => updateEditForm("website", e.target.value)} />
              </div>
              <div className="launchpad-form__group">
                <label>Whitepaper</label>
                <input type="url" placeholder="https://" value={editData.whitepaper} onChange={(e) => updateEditForm("whitepaper", e.target.value)} />
              </div>
              <div className="launchpad-form__group">
                <label>Description</label>
                <textarea rows={4} maxLength={DESCRIPTION_MAX} value={editData.description} onChange={(e) => updateEditForm("description", e.target.value)} />
              </div>
              {Object.keys(formErrors).length > 0 && (
                <div className="launchpad-form__err-block">
                  {Object.entries(formErrors).map(([k, v]) => v && <span key={k}>{v}</span>)}
                </div>
              )}
              <div className="launchpad-modal__actions">
                <button type="button" className="launchpad-btn launchpad-btn--reset" onClick={closeEdit}>Cancel</button>
                <button type="submit" className="launchpad-btn launchpad-btn--publish" disabled={submitLoading}>
                  {submitLoading ? <><i className="fas fa-spinner fa-spin" /> Saving...</> : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default LaunchpadManagementPage;
